import { Add, Close, EditOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Button as MuiButton, Skeleton, Stack, Tooltip } from '@mui/material';
import Autocomplete, { AutocompleteCloseReason, autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import copy from 'fast-copy';
import * as React from 'react';
import { TagInfo } from '../../interfaces';
import { useGetTagsBasedOnParameterQuery, useLazyGetTagGroupsQuery } from '../../redux/services';
import { AddEditTags } from '../../screens/tags/tagListing/addEditTags';
import { hexToGrayscale } from '../../utils';
import { CanonicalKeys } from '../tagsDropdown';

interface PopperComponentProps {
    anchorEl?: any;
    disablePortal?: boolean;
    open: boolean;
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: 'none',
        margin: 0,
        color: 'inherit',
        fontSize: 13,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: '#fff',

        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: 'auto',
            alignItems: 'flex-start',
            padding: 8,
            borderBottom: `1px solid  ${' #eaecef'}`,

            '&[aria-selected="true"]': {
                backgroundColor: 'transparent',
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
            {
                backgroundColor: theme.palette.action.hover,
            },
            ...theme.applyStyles('dark', {
                borderBottom: `1px solid  ${'#30363d'}`,
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: '#1c2128',
        }),
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: 'relative',
    },
}));

function PopperComponent(props: PopperComponentProps) {
    const { disablePortal, anchorEl, open, ...other } = props;
    return <StyledAutocompletePopper {...other} />;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
    border: `1px solid ${'#e1e4e8'}`,
    boxShadow: `0 8px 24px ${'rgba(149, 157, 165, 0.2)'}`,
    color: '#24292e',
    backgroundColor: '#fff',
    borderRadius: 6,
    width: 300,
    zIndex: theme.zIndex.modal,
    fontSize: 13,
    ...theme.applyStyles('dark', {
        border: `1px solid ${'#30363d'}`,
        boxShadow: `0 8px 24px ${'rgb(1, 4, 9)'}`,
        color: '#c9d1d9',
        backgroundColor: '#1c2128',
    }),
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
    padding: 10,
    width: '100%',
    borderBottom: `1px solid ${'#30363d'}`,
    '& input': {
        borderRadius: 4,
        backgroundColor: '#fff',
        border: `1px solid ${'#30363d'}`,
        padding: 8,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        fontSize: 14,
        '&:focus': {
            boxShadow: `0px 0px 0px 3px ${'rgba(3, 102, 214, 0.3)'}`,
            borderColor: '#0366d6',
            ...theme.applyStyles('dark', {
                boxShadow: `0px 0px 0px 3px ${'rgb(12, 45, 107)'}`,
                borderColor: '#388bfd',
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: '#0d1117',
            border: `1px solid ${'#eaecef'}`,
        }),
    },
    ...theme.applyStyles('dark', {
        borderBottom: `1px solid ${'#eaecef'}`,
    }),
}));

const Button = styled(ButtonBase)(({ theme }) => ({
    fontSize: 13,
    width: '100%',
    textAlign: 'left',
    paddingTop: '4px',
    paddingBottom: '2px',
    fontWeight: 500,
    borderTopRightRadius: '4px',
    borderTopLeftRadius: '4px',
    fontFamily: 'htrts_medium',
    '&:hover,&:focus': {
        paddingLeft: '4px',
        paddingRight: '4px',
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    '& span': {
        width: '100%',
    },
    '& svg': {
        width: 16,
        height: 16,
    },
}));

export const GitHubLabel: React.FC<{
    title: string,
    loading?: boolean,
    tags?: Partial<TagInfo>[],
    multiple?: boolean,
    tagCanonicalName?: CanonicalKeys,
    onUpdate?: (tags: Partial<TagInfo>[]) => any,
}> = ({
    title = '',
    loading = false,
    tags = [],
    multiple = false,
    tagCanonicalName,
    onUpdate = (tags: Partial<TagInfo>[]) => false,
}) => {

        const tagsData = useGetTagsBasedOnParameterQuery(tagCanonicalName, { skip: !tagCanonicalName });

        const [getTagGroupBasedOnCanonicalName, { data: allData = { data: [], totalCount: 0 } }] = useLazyGetTagGroupsQuery();

        const { data } = allData;

        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const [labels, setLabels] = React.useState<Partial<TagInfo>[]>(tags);
        const [pendingValue, setPendingValue] = React.useState<Partial<TagInfo>[]>(tags);
        const [openDialog, setOpen] = React.useState(false);

        const isLoading = tagsData?.isLoading || tagsData?.isFetching;

        const options = tagsData?.data?.tags ?? [];

        const groupDetails = options[0] || data[0];

        const selectedTagGroup = groupDetails ? { label: groupDetails.tag_group_name, value: groupDetails.tag_group_id ?? groupDetails._id, _id: groupDetails.tag_group_id ?? groupDetails._id } : undefined;

        const handleOpen = () => setOpen(true);

        const closeDialog = (newTag?: TagInfo[]) => {
            if (newTag) {
                onUpdate(newTag);
            }
            setOpen(false)
        };

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            setPendingValue(labels);
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setLabels(pendingValue);
            if (anchorEl) {
                anchorEl.focus();
            }
            setAnchorEl(null);
            onUpdate(pendingValue);
        };

        const open = Boolean(anchorEl);
        const id = open ? 'github-label' : undefined;

        const updatedTitle = isLoading ? "Loading..." : selectedTagGroup?.label || tagCanonicalName;

        React.useEffect(() => {
            setLabels(tags);
            setPendingValue(tags);
            // eslint-disable-next-line
        }, [tags]);

        React.useEffect(() => {
            if (tagCanonicalName && options.length === 0 && tagsData?.isSuccess) {
                getTagGroupBasedOnCanonicalName({ canonical_name: tagCanonicalName });
            }
            // eslint-disable-next-line
        }, [tagCanonicalName, tagsData?.isSuccess]);

        return (
            <React.Fragment>

                <Box>

                    <Button onClick={handleClick}>
                        <span>{updatedTitle}</span>
                        <EditOutlined fontSize='small' />
                    </Button>

                    <Divider sx={{ mt: '1px' }} />

                    {!loading && <Stack gap={1} direction={"row"} sx={{ mt: 1, flexWrap: "wrap" }}>
                        {labels.map((label) => (
                            <Chip variant="outlined" sx={{
                                fontSize: '0.65rem',
                                borderColor: label?.tag_color,
                                backgroundColor: label?.tag_color,
                                height: `20px`,
                                cursor: "default",
                                color: (theme) => `${hexToGrayscale(label?.tag_color) > 150 ? theme.palette.text.secondary : "#FFFFFF"}`
                            }}
                                size={"small"}
                                label={label?.tag_name ?? ""}
                            />
                        ))}
                    </Stack>}

                    {loading && <Skeleton width={"100px"} />}

                </Box>

                <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start">
                    <ClickAwayListener onClickAway={handleClose}>
                        <div>
                            <Box sx={(t) => ({
                                borderBottom: `1px solid ${'#30363d'}`,
                                padding: '8px 10px',
                                fontWeight: 600,
                                ...t.applyStyles('light', {
                                    borderBottom: `1px solid ${'#eaecef'}`,
                                }),
                            })}>
                                <Stack direction={"row"} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                                    <Stack direction={"row"} sx={{ alignItems: "center", gap: 1 }}>
                                        Select {updatedTitle}
                                        {selectedTagGroup &&
                                            <Tooltip title={`Create a tag for ${updatedTitle}`}>
                                                <MuiButton
                                                    color="primary"
                                                    onClick={handleOpen}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ px: 0, minWidth: 'unset', width: 16, height: 16 }}
                                                >
                                                    <Add htmlColor="primary" sx={{ fontSize: 14 }} />
                                                </MuiButton>
                                            </Tooltip>
                                        }
                                    </Stack>

                                    <IconButton
                                        aria-label="close"
                                        onClick={handleClose}
                                        sx={{
                                            backgroundColor: (theme) => theme.palette.background.paper,
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            outline: "none",
                                            transition: "box-shadow 0.3s ease-in-out",
                                            ":hover, :focus": {
                                                boxShadow: "0px 0px 5px 2px rgba(103, 58, 183, 0.5)",
                                            },
                                            color: (theme) => theme.palette.grey[500],
                                            width: "25px",
                                            height: "25px"
                                        }}
                                        size='small'
                                    >
                                        <Close fontSize='small' />
                                    </IconButton>
                                </Stack>
                            </Box>

                            <Autocomplete
                                open
                                multiple
                                onClose={(e: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                                    if (reason === 'escape') {
                                        handleClose();
                                    }
                                }}
                                value={pendingValue}
                                onChange={(event, newValue, reason) => {
                                    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Backspace' || (event as React.KeyboardEvent).key === 'Delete') &&
                                        reason === 'removeOption'
                                    ) {
                                        return;
                                    }

                                    if (multiple) {
                                        setPendingValue(newValue);
                                    } else {
                                        setPendingValue(newValue.length === 0 ? newValue : [newValue.at(-1) ?? {}])
                                    }
                                }}
                                disableCloseOnSelect
                                renderTags={() => null}
                                noOptionsText="No Tags"
                                isOptionEqualToValue={(option, value) => {
                                    return option._id === value._id;
                                }}
                                renderOption={(props, option, state) => {

                                    const { selected } = state;
                                    const { key, ...optionProps } = props;

                                    return (
                                        <li key={key} {...optionProps}>

                                            <Box
                                                component={DoneIcon}
                                                sx={{ width: 17, height: 17, mr: '5px', ml: '-2px' }}
                                                style={{ visibility: selected ? 'visible' : 'hidden' }}
                                            />

                                            <Box component="span" sx={{
                                                width: 14,
                                                height: 14,
                                                flexShrink: 0,
                                                borderRadius: '3px',
                                                mr: 1,
                                                mt: '2px',
                                            }} style={{ backgroundColor: option.tag_color }} />

                                            <Box
                                                sx={(t) => ({
                                                    flexGrow: 1,
                                                    '& span': {
                                                        color: '#8b949e',
                                                        ...t.applyStyles('light', {
                                                            color: '#586069',
                                                        }),
                                                    },
                                                })}
                                            >
                                                {option.tag_name}
                                                <br />
                                                <span>{option.tag_desc}</span>
                                            </Box>

                                            <Box
                                                component={CloseIcon}
                                                sx={{ opacity: 0.6, width: 18, height: 18 }}
                                                style={{
                                                    visibility: selected ? 'visible' : 'hidden',
                                                }}
                                            />

                                        </li>
                                    );
                                }}
                                options={copy(options)?.sort((a: Partial<TagInfo>, b: Partial<TagInfo>) => {
                                    // Display the selected labels first.
                                    let ai = labels.indexOf(a);
                                    ai = ai === -1 ? labels.length + options.indexOf(a) : ai;
                                    let bi = labels.indexOf(b);
                                    bi = bi === -1 ? labels.length + options.indexOf(b) : bi;
                                    return ai - bi;
                                }) ?? []}
                                loading={tagsData?.isLoading || tagsData?.isFetching}
                                getOptionLabel={(option) => {
                                    return option?._id ?? ""
                                }}
                                renderInput={(params) => (
                                    <StyledInput
                                        ref={params.InputProps.ref}
                                        inputProps={params.inputProps}
                                        autoFocus
                                        placeholder="Filter tags"
                                    />
                                )}
                                slots={{
                                    popper: PopperComponent,
                                }}
                            />
                        </div>
                    </ClickAwayListener>
                </StyledPopper>

                <Dialog
                    key={JSON.stringify(pendingValue)}
                    maxWidth={"sm"}
                    fullWidth={true}
                    open={openDialog}
                    onClose={() => closeDialog()}
                >
                    <>
                        {/* Dialog Title */}
                        <DialogTitle sx={{ textAlign: 'center' }}>Create Tag</DialogTitle>
                        <IconButton
                            aria-label="close"
                            onClick={() => closeDialog()}
                            sx={{
                                backgroundColor: (theme) => theme.palette.background.paper,
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                                outline: "none",
                                transition: "box-shadow 0.3s ease-in-out",
                                ":hover, :focus": {
                                    boxShadow: "0px 0px 5px 2px rgba(103, 58, 183, 0.5)",
                                },
                                position: "absolute",
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                                width: "25px",
                                height: "25px",
                                marginTop: "10px",
                                marginRight: "11px",
                            }}
                        >
                            <Close />
                        </IconButton>
                    </>
                    <DialogContent>
                        <AddEditTags isEdit={false} data={null} refetch={tagsData?.refetch} selectedTagGroup={selectedTagGroup} onDialogClose={(newTag) => closeDialog(multiple ? [...pendingValue, newTag] : [newTag])} />
                    </DialogContent>
                </Dialog>
            </React.Fragment>
        );
    }
